/**
 * Navigation Bar Component for WoodAlps Website
 * 
 * Advanced responsive navigation with the following features:
 * - Scroll-based behavior (hide on scroll down, show on scroll up)
 * - Dynamic background transparency based on scroll position
 * - Shopping cart count display with real-time updates
 * - Mobile-responsive hamburger menu
 * - Multi-level dropdown navigation
 * - Dynamic logo integration from Sanity CMS
 * 
 * State Management:
 * - Mobile menu toggle state
 * - Scroll direction and position tracking
 * - Cart item count from localStorage
 * - Logo URL and dimensions from CMS
 * 
 * @description Responsive navigation bar with scroll behavior and cart integration
 */

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { Menu, X, ShoppingCart, ChevronDown } from 'lucide-react';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import { useTranslation } from '../../src/hooks/useTranslation';

const Navbar = ({ transparent = false, headerShadow = false, siteSettings = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [scrollDirection, setScrollDirection] = useState('up');
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef(null);
  const { t, isLoading: translationsLoading } = useTranslation();
  const router = useRouter();
  const isHomePage = router.pathname === '/';

  // Get logo details from site settings or use defaults
  const logoUrl = siteSettings?.logo ? urlForImage(siteSettings.logo) : null;
  const logoSrc = logoUrl && typeof logoUrl === 'string' && logoUrl.trim() !== '' 
    ? logoUrl 
    : "/images/logoWA.png";
  const logoWidth = siteSettings?.logoWidth || 120;
  const logoHeight = siteSettings?.logoHeight || 60;
  const logoAlt = siteSettings?.logo?.alt || "WoodAlps";

  // Advanced scroll detection and direction tracking for navbar behavior
  useEffect(() => {
    const handleScroll = () => {
      // Determine if navbar should have background (scrolled past threshold)
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      
      // Calculate scroll direction for hide/show navbar behavior
      const currentScrollY = window.scrollY;
      const direction = currentScrollY > lastScrollY ? 'down' : 'up';
      
      // Only update direction if scroll difference is significant
      // This prevents jittery behavior from minor scroll variations
      if (Math.abs(currentScrollY - lastScrollY) > 10) {
        setScrollDirection(direction);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    // Set initial state on component mount
    handleScroll();
    
    // Attach scroll listener with cleanup
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Effect to handle body scrolling when mobile menu is open
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (isOpen) {
        // Prevent scrolling on the body when menu is open
        document.body.style.overflow = 'hidden';
        document.body.style.height = '100%';
      } else {
        // Restore scrolling when menu is closed
        document.body.style.overflow = '';
        document.body.style.height = '';
      }
    }
    
    return () => {
      // Clean up on unmount
      if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
        document.body.style.height = '';
      }
    };
  }, [isOpen]);
  
  // Load cart count from localStorage when on client side
  useEffect(() => {
    const updateCartCount = () => {
      if (typeof window !== 'undefined') {
        try {
          const cartData = JSON.parse(localStorage.getItem('woodalps-cart') || '[]');
          setCartCount(cartData.length);
        } catch (error) {
          console.error('Error loading cart data:', error);
          setCartCount(0);
        }
      }
    };
    
    // Initial load
    updateCartCount();
    
    // Listen for storage events to update cart count when cart is modified elsewhere
    window.addEventListener('storage', updateCartCount);
    
    // Custom event for cart updates within the same page
    window.addEventListener('cart-updated', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cart-updated', updateCartCount);
    };
  }, []);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    // Cleanup function
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);
  
  // Define main navigation links with translations
  const navLinks = [
    { name: t('navigation.road'), path: '/road' },
    { name: t('navigation.gravel'), path: '/gravel' },
    { name: t('navigation.mtb'), path: '/mtb' },
    { name: t('navigation.blog'), path: '/blog' },
    { 
      name: t('navigation.explore_us'), 
      path: '#',
      hasDropdown: true,
      dropdownItems: [
        { name: t('navigation.our_history'), path: '/about' },
        { name: t('navigation.ambassadors'), path: '/ambassadors' },
        { name: t('navigation.design_engineering'), path: '/design-and-engineering' },
        { name: t('navigation.gallery'), path: '/gallery' },
        
      ]
    },
    { 
      name: t('navigation.support'), 
      path: '#',
      hasDropdown: true,
      dropdownItems: [
        { name: t('navigation.faq'), path: '/faq' },
        { name: t('navigation.contact_us'), path: '/contact' },
      ]
    },
  ];

  const languages = [
    { code: 'en', name: t('languages.english') },
    { code: 'fr', name: t('languages.french') },
    { code: 'de', name: t('languages.german') },
    { code: 'it', name: t('languages.italian') },
  ];

  // Get current language from router
  const currentLanguage = router.locale || 'en';
  const currentLanguageData = languages.find(lang => lang.code === currentLanguage);

  // Language switching function
  const handleLanguageChange = (langCode) => {
    const currentPath = router.asPath;
    router.push(currentPath, currentPath, { locale: langCode });
    setIsLanguageDropdownOpen(false);
  };





  // Determine whether to use transparency based on both the isHomePage and transparent prop
  const shouldBeTransparent = transparent;

  // Only show shadow when background is NOT transparent (when scrolled or transparency is disabled)
  const showShadow = isScrolled || !shouldBeTransparent;

  // Determine header visibility based on scroll direction for mobile
  const headerTransform = scrollDirection === 'down' && isScrolled ? 'translateY(-100%)' : 'translateY(0)';

  const headerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    zIndex: 50,
    height: '70px', // Fixed height instead of percentage for better control
    backgroundColor: isScrolled || !shouldBeTransparent ? 'black' : 'transparent',
    boxShadow: 'none', // No shadow on main header
    transition: 'background-color 0.3s, transform 0.3s ease-in-out',
  };

  // Apply the mobile-only transform styles using a separate class
  const mobileTransformClass = scrollDirection === 'down' && isScrolled ? 'md:transform-none -translate-y-full' : 'transform-none';

  return (
    <header 
      style={headerStyle} 
      className={`transition-transform duration-300 ease-in-out ${mobileTransformClass}`}
    >
      {/* Main Header - Fixed height */}
      <div className="container-custom flex justify-between items-center h-full" style={{ opacity: translationsLoading ? 0 : 1, transition: 'opacity 0.2s ease-in-out' }}>
        {/* Logo - Responsive positioning and sizing */}
        <div className="absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2">
          <Link href="/" className="z-0">
            <div className="relative flex items-center">
              <Image
                src={logoSrc}
                alt={logoAlt}
                loader={sanityLoader}
                width={logoWidth}
                height={logoHeight}
                className="object-contain w-[65px] h-[32px] sm:w-[80px] sm:h-[40px] md:w-[100px] md:h-[50px] lg:w-[120px] lg:h-[60px]"
                priority
              />
            </div>
          </Link>
        </div>

        {/* Right-side - Empty space for mobile menu button positioning */}
        <div className="hidden md:block ml-auto"></div>

        {/* Mobile Menu Button */}
        <div className="md:hidden z-20 ml-auto">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-white focus:outline-none"
          >
            {isOpen ? <X size={18} className="sm:size-[20px]" /> : <Menu size={18} className="sm:size-[20px]" />}
          </button>
        </div>
      </div>

      {/* Navigation Links - Modified to be below the header */}
      <div className="hidden md:block absolute left-0 right-0 h-[40px]" 
           style={{ 
             top: '70px', 
             backgroundColor: isScrolled || !shouldBeTransparent ? 'black' : 'transparent',
             transition: 'background-color 0.3s, opacity 0.2s ease-in-out',
             boxShadow: showShadow ? '0 4px 10px rgba(0, 0, 0, 0.4)' : 'none', // Shadow only when background is not transparent
             opacity: translationsLoading ? 0 : 1,
           }}>
        {/* White line above nav links */}
        <div className="w-1/2 h-[1px] bg-white mx-auto"></div>   
        <div className="container-custom h-full flex items-center justify-between">
          {/* Navigation Links */}
          <ul className="flex justify-center items-center flex-1 tracking-wide text-[10px] sm:text-xs md:text-sm lg:text-base gap-2 md:gap-4 lg:gap-6 font-montserrat">
            {navLinks.map((link, index) => (
              <li key={index} className="relative group px-2 sm:px-3 md:px-4 lg:px-5 mx-2 py-2">
                {link.hasDropdown ? (
                  <div className="relative">
                    <button className="flex items-center text-white hover:text-accent-color group-hover:text-accent-color whitespace-nowrap py-1">
                      {link.name}
                      <ChevronDown size={12} className="ml-1 md:size-[14px]" />
                    </button>

                    <div className="absolute left-0 mt-2 w-40 sm:w-48 md:w-56 lg:w-60 bg-black shadow-lg border border-white/10 rounded-md overflow-hidden origin-top transition-all duration-300 ease-in-out z-50 group-hover:opacity-100 group-hover:scale-y-100 group-hover:visible opacity-0 scale-y-0 invisible"
                         style={{
                           transformOrigin: 'top center'
                         }}>
                      {link.dropdownItems.map((item, idx) => (
                        <Link
                          key={idx}
                          href={item.path}
                          className="block px-2 py-2 sm:px-3 sm:py-2.5 text-white hover:text-accent-color hover:bg-white/10 transition-colors text-[10px] sm:text-xs md:text-sm font-montserrat"
                          onClick={() => setIsOpen(false)}
                        >
                          <span>{item.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    href={link.path}
                    className="text-white hover:text-accent-color relative whitespace-nowrap"
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                    <span className="absolute left-0 bottom-[-5px] w-full h-0.5 bg-accent-color transform scale-x-0 transition-transform duration-300 group-hover:scale-x-100"></span>
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Right-side items: Language Selector and Cart */}
          <div className="flex items-center space-x-4">
            {/* Language Selector */}
            {/* Language Switcher */}
            <div ref={languageDropdownRef} className="relative mr-4 language-dropdown group">
              <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center text-white hover:text-accent-color group-hover:text-accent-color font-montserrat py-1"
              >
                <span className="hidden sm:inline text-[10px] sm:text-xs md:text-sm uppercase">
                  {currentLanguage }
                </span>
                <ChevronDown size={12} className="ml-1" />
              </button>
              
              {/* Language Dropdown */}
              <div 
                className={`absolute right-0 top-full mt-2 bg-black shadow-lg border border-white/10 rounded-md overflow-hidden origin-top transition-all duration-300 ease-in-out z-50 min-w-[120px] ${
                  isLanguageDropdownOpen 
                    ? 'opacity-100 scale-y-100 visible' 
                    : 'opacity-0 scale-y-0 invisible group-hover:opacity-100 group-hover:scale-y-100 group-hover:visible'
                }`}
                style={{
                  transformOrigin: 'top center'
                }}
              >
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className={`block w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors ${
                      currentLanguage === language.code 
                        ? 'text-accent-color bg-white/5' 
                        : 'text-white'
                    }`}
                  >
                    {language.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Cart Link */}
            <Link 
              href="/cart" 
              className="flex items-center text-white hover:text-accent-color font-montserrat relative"
            >
              <ShoppingCart size={14} className="sm:size-[16px] md:size-[18px]" />
              {cartCount > 0 && (
                <span className="ml-1 -mt-2 bg-accent-color text-white text-[8px] md:text-[10px] rounded-full h-4 w-4 md:h-5 md:w-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Improved with independent scrolling and reorganized sections */}
      {isOpen && !translationsLoading && (
        <div className="md:hidden fixed inset-0 bg-black z-10">
          {/* Mobile menu content with independent scrolling */}
          <div className="h-full overflow-y-auto pt-20 pb-10">
            {/* Inner container for padding */}
            <div className="px-8">
              {/* Quick access section - Cart & Language at top */}
              <div className="mb-8 border-b border-white/20 pb-6">
                {/* Mobile Cart Link */}
                <div className="mb-6">
                  <Link 
                    href="/cart" 
                    className="flex items-center justify-between text-base sm:text-lg text-white hover:text-accent-color px-3 py-3 bg-black/20 rounded-md"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="flex items-center">
                      <ShoppingCart size={20} className="mr-3" />
                      <span>Cart</span>
                    </div>
                    {cartCount > 0 && (
                      <span className="bg-accent-color text-white text-xs rounded-full h-6 w-6 flex items-center justify-center">
                        {cartCount}
                      </span>
                    )}
                  </Link>
                </div>
                
                {/* Mobile Language Selector */}
                <div>
                  <h3 className="text-white text-base mb-3 flex items-center">
                    Language: {currentLanguageData?.name || 'English'}
                  </h3>
                  <div className="space-y-2">
                    {languages.filter(lang => lang.code !== currentLanguage).map(lang => (
                      <button 
                        key={lang.code}
                        onClick={() => {
                          handleLanguageChange(lang.code);
                          setIsOpen(false);
                        }}
                        className="flex items-center w-full text-sm sm:text-base text-white/90 hover:text-accent-color text-left px-3 py-2 hover:bg-black/20 rounded-md transition-colors"
                      >
                        <span className="mr-3 font-bold">{lang.code.toUpperCase()}</span>
                        {lang.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Main Navigation Links */}
              <ul className="flex flex-col space-y-6 font-montserrat">
                {navLinks.map((link, index) => (
                  <li key={index} className="relative">
                    {link.hasDropdown ? (
                      <div className="relative mb-4">
                        <button 
                          className="flex items-center justify-between w-full text-base sm:text-lg text-white px-3 py-2 hover:bg-black/20 rounded-md transition-colors"
                          onClick={() => {
                            // Toggle dropdown for mobile
                            const dropdown = document.getElementById(`mobile-dropdown-${index}`);
                            if (dropdown) {
                              dropdown.classList.toggle('hidden');
                            }
                          }}
                        >
                          <span>{link.name}</span>
                          <ChevronDown size={18} />
                        </button>
                        
                        {/* Mobile Dropdown */}
                        <div id={`mobile-dropdown-${index}`} className="hidden mt-2 pl-5 space-y-3">
                          {link.dropdownItems.map((item, idx) => (
                            <Link
                              key={idx}
                              href={item.path}
                              className="block py-2 text-sm sm:text-base text-white/80 hover:text-accent-color pl-3 border-l border-white/20"
                              onClick={() => setIsOpen(false)}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        href={link.path}
                        className="flex items-center text-base sm:text-lg text-white hover:text-accent-color px-3 py-2 hover:bg-black/20 rounded-md transition-colors"
                        onClick={() => setIsOpen(false)}
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar; 